import {
  Action,
  editorRootElementRef$,
  linkDialogState$,
  switchFromPreviewToLinkEdit$,
  updateLink$,
  useCellValues,
  usePublisher,
  withLatestFrom,
} from "@mdxeditor/editor";
import * as Popover from "@radix-ui/react-popover";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useOnClickOutside } from "../../../hooks/useOnClickOutside";
import Button from "../button/Button";
import { Input } from "../inputs/Input";

const LinkEditForm: React.FC<{
  linkDialogState: any;
  initialUrl: string | undefined;
}> = ({ linkDialogState, initialUrl }) => {
  const { t } = useTranslation();
  const [url, setUrl] = useState(initialUrl || "");
  const updateLink = usePublisher(updateLink$);
  // const cancelLinkEdit = usePublisher(cancelLinkEdit$);
  const closeLinkDialog = usePublisher(closeLinkDialog$);
  const switchFromPreviewToLinkEdit = usePublisher(
    switchFromPreviewToLinkEdit$,
  );

  // useEffect(() => {
  //   console.log("initialUrl", initialUrl, linkDialogState?.type);
  //   if(linkDialogState?.type === "preview") {
  //     switchFromPreviewToLinkEdit();
  //   }
  // }, [linkDialogState?.type]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Ensure URL has a protocol if missing
    let formattedUrl = url;
    if (
      formattedUrl &&
      !formattedUrl.startsWith("http://") &&
      !formattedUrl.startsWith("https://") &&
      !formattedUrl.startsWith("mailto:") &&
      !formattedUrl.startsWith("tel:")
    ) {
      formattedUrl = `https://${formattedUrl}`;
    }

    // Update the link with URL only
    updateLink({
      title: "",
      url: formattedUrl,
    });

    setTimeout(() => {
      closeLinkDialog();
    }, 100);
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleSubmit(e as any);
  };

  const handleReset = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    closeLinkDialog();
  };

  const handleRemoveLink = (e: React.MouseEvent) => {
    if (linkDialogState.type === "preview") {
      switchFromPreviewToLinkEdit();
    }
    e.preventDefault();
    e.stopPropagation();
    updateLink({
      title: "",
      url: "",
    });
    setTimeout(() => {
      closeLinkDialog();
    }, 100);
  };

  return (
    <form
      onSubmit={handleSubmit}
      onReset={handleReset}
      className="flex flex-col gap-2 p-2 min-w-[320px]"
    >
      <div className="flex flex-col gap-1">
        {/* <h3 className="heading-sm">URL verlinken</h3> */}
        <Input
          id="url"
          name="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="example.com"
          leadingIcon={
            url.startsWith("mailto:")
              ? "mail"
              : url.startsWith("tel:")
                ? "phone"
                : "link"
          }
          onFocus={() => {
            if (linkDialogState.type === "preview") {
              switchFromPreviewToLinkEdit();
            }
          }}
        />
      </div>

      <div className="flex justify-between gap-2 mt-2">
        {initialUrl && (
          <Button critical leadingIcon="trash" onClick={handleRemoveLink} />
        )}

        <div className="flex gap-2 ml-auto">
          <Button type="reset" text={t("global:cancel")} />
          <Button
            type="submit"
            variant="primary"
            disabled={!url}
            text={t("global:save")}
            onClick={handleButtonClick}
          />
        </div>
      </div>
    </form>
  );
};

const closeLinkDialog$ = Action((r) => {
  r.sub(
    r.pipe(closeLinkDialog$, withLatestFrom(linkDialogState$)),
    ([, state]) => {
      if (state.type !== "inactive") {
        r.pub(linkDialogState$, {
          type: "inactive",
        });
      }
    },
  );
});

// const LinkPreview: React.FC<{
//   initialUrl: string | undefined;
// }> = ({ initialUrl }) => {
//   const switchFromPreviewToLinkEdit = usePublisher(
//     switchFromPreviewToLinkEdit$
//   );
//   const updateLink = usePublisher(updateLink$);
//   const closeLinkDialog = usePublisher(closeLinkDialog$);

//   const handleEditLink = (e: React.MouseEvent) => {
//     e.preventDefault();
//     e.stopPropagation();
//     switchFromPreviewToLinkEdit();
//   };

//   const handleRemoveLink = (e: React.MouseEvent) => {
//     switchFromPreviewToLinkEdit();
//     e.preventDefault();
//     e.stopPropagation();
//     updateLink({
//       title: "",
//       url: ""
//     });
//     setTimeout(() => {
//       closeLinkDialog()
//     }, 100);
//   };
//   return <div className="p-2 flex gap-2">{initialUrl}

//   <Button

//           leadingIcon="edit"
//           onClick={handleEditLink}
//         />
//   <Button
//           critical
//           leadingIcon="trash"
//           onClick={handleRemoveLink}
//         />

// </div>;
// };

const InsertLinkPopover: React.FC = () => {
  const [linkDialogState, editorRootElementRef] = useCellValues(
    linkDialogState$,
    editorRootElementRef$,
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const closeLinkDialog = usePublisher(closeLinkDialog$);
  const [hasActiveSelection, setHasActiveSelection] = useState(false);

  const shouldShowPopover =
    linkDialogState.type === "edit" ||
    (linkDialogState.type === "preview" && !hasActiveSelection);

  // Check for active selection
  useEffect(() => {
    const checkSelection = () => {
      const selection = window.getSelection();
      const hasSelection = selection && selection.toString().length > 0;
      setHasActiveSelection(!!hasSelection);
    };

    // Check immediately
    checkSelection();

    document.addEventListener("mousedown", checkSelection);
    document.addEventListener("mouseup", checkSelection);
    document.addEventListener("selectionchange", checkSelection);

    return () => {
      document.removeEventListener("mousedown", checkSelection);
      document.removeEventListener("mouseup", checkSelection);
      document.removeEventListener("selectionchange", checkSelection);
    };
  }, []);

  useOnClickOutside(contentRef, () => {
    if (linkDialogState.type !== "inactive") {
      closeLinkDialog();
    }
  });

  useEffect(() => {
    if (
      (linkDialogState.type === "edit" || linkDialogState.type === "preview") &&
      !linkDialogState.url
    ) {
      setTimeout(() => {
        document.getElementById("url")?.focus();
      }, 0);
    }
  }, [linkDialogState.type, linkDialogState.url]);

  return (
    <Popover.Root modal={true} open={shouldShowPopover}>
      <Popover.Portal container={editorRootElementRef?.current}>
        <Popover.Content
          className="z-50 bg-surface-default rounded-2xl shadow-lg border border-border-default"
          onOpenAutoFocus={(e) => {
            e.preventDefault();
          }}
          key={linkDialogState.linkNodeKey}
          style={{
            position: "absolute",
            top: linkDialogState?.rectangle?.top
              ? linkDialogState?.rectangle?.top + 23
              : 0,
            left: linkDialogState?.rectangle?.left
              ? linkDialogState?.rectangle?.left
              : 0,
          }}
          ref={contentRef}
        >
          <LinkEditForm
            linkDialogState={linkDialogState}
            initialUrl={
              linkDialogState?.initialUrl || linkDialogState?.url || ""
            }
          />

          {/* {linkDialogState.type === "preview" && <LinkPreview 
            initialUrl={linkDialogState.url }
          />} */}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default InsertLinkPopover;
